'use client'
import React from 'react'

import { X, FileCode, FileJson, FileType, File, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFileStore } from '@/lib/file-store'
import { Button } from '@/components/ui/button'
import { FileVersionSelector } from '@/components/ide/FileVersionSelector'
import { Badge } from '@/components/ui/badge'

const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return <FileCode className="h-3.5 w-3.5 text-yellow-400" />
    case 'json':
      return <FileJson className="h-3.5 w-3.5 text-yellow-600" />
    case 'css':
    case 'scss':
    case 'sass':
      return <FileType className="h-3.5 w-3.5 text-blue-400" />
    case 'html':
      return <FileType className="h-3.5 w-3.5 text-orange-400" />
    case 'md':
    case 'mdx':
      return <FileText className="h-3.5 w-3.5 text-blue-300" />
    default:
      return <File className="h-3.5 w-3.5 text-muted-foreground" />
  }
}

export function EditorTabs() {
  const { openFiles, activeFile, setActiveFile, closeFile, getFileNode, updateFileContent } = useFileStore()
  const [isViewingHistoricalVersion, setIsViewingHistoricalVersion] = React.useState(false)
  const [activeFileVersion, setActiveFileVersion] = React.useState<number | null>(null)

  // Get active file metadata
  const activeFileNode = activeFile ? getFileNode(activeFile) : null
  const activeFileId = activeFileNode?.id || null
  const activeFileCurrentVersion = activeFileNode?.currentVersion || null

  if (openFiles.length === 0) {
    return null
  }

  return (
    <div className="flex items-center justify-between bg-[color:var(--tab-inactive)] border-b border-border">
      <div className="flex items-center overflow-x-auto scrollbar-hide">
        {openFiles.map((path) => {
          const fileName = path.split('/').pop() || path
          const isActive = activeFile === path
          
          return (
            <div
              key={path}
              className={cn(
                'flex items-center gap-2 px-3 py-2 border-r border-border cursor-pointer group min-w-fit',
                'hover:bg-[color:var(--tab-active)] transition-colors',
                isActive && 'bg-[color:var(--tab-active)] border-t-2 border-t-primary'
              )}
              onClick={() => setActiveFile(path)}
            >
              {getFileIcon(fileName)}
              <span className="text-sm text-foreground whitespace-nowrap">{fileName}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 hover:bg-muted ml-1"
                onClick={(e) => {
                  e.stopPropagation()
                  closeFile(path)
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )
        })}
      </div>
      
      {/* Version selector for active file */}
      {activeFile && activeFileId && (
        <div className="flex items-center gap-2 px-3 py-1 border-l border-border">
          {isViewingHistoricalVersion && (
            <Badge variant="outline" className="text-xs">
              Viewing History (Read-Only)
            </Badge>
          )}
          <FileVersionSelector
            fileId={activeFileId}
            currentVersion={activeFileVersion || activeFileCurrentVersion || undefined}
            onVersionChange={(versionNumber, content) => {
              setActiveFileVersion(versionNumber)
              
              // Check if viewing historical version
              const isHistorical = versionNumber !== activeFileCurrentVersion
              setIsViewingHistoricalVersion(isHistorical)
              
              // Update file content with version content
              if (content && activeFile) {
                updateFileContent(activeFile, content)
              }
            }}
          />
        </div>
      )}
    </div>
  )
}
